import React, { useCallback, useContext, useEffect, useState } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import LockclosedIcon from 'icons/Lockclosed.icon';
import ShareableLinkLockScreen from '../ShareableLinkLockScreen';
import { Button, Loader } from 'ncoded-component-library';
import api from 'api';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import ShareableLinkNdaProcessing from '../ShareableLinkNdaProcessing';
import { AxiosError } from 'axios';
import { useNavigate } from 'react-router-dom';
import { AGREEMENT_ERROR_CODES, NdaProvider } from 'models/Agreement';
import {
  getAgreementErrorCode,
  getIsNativeNda,
  getSigningPathWithReturnPath,
} from 'agreementUtils';
import { getShareableLinkRoute } from 'utils';

import './ShareableLinkNdaSign.styles.scss';

type ShareableLinkNdaSignProps = {
  className?: string;
  shareableLinkId: string;
  isProcessing: boolean;
  ndaProvider?: NdaProvider;
  shouldStartSigning?: boolean;
  onRefetch?: () => void;
};

const ShareableLinkNdaSign: React.FC<ShareableLinkNdaSignProps> = (props) => {
  const {
    className,
    shareableLinkId,
    isProcessing,
    ndaProvider,
    shouldStartSigning,
    onRefetch,
  } = props;

  const [isLoading, setIsLoading] = useState(false);

  const { popServerError } = useContext(PopNotificationsContext);

  const navigate = useNavigate();

  const classes = classNames('shareable-link-nda-sign', className);

  const { t } = useTranslation();

  const isNativeNda = getIsNativeNda({ ndaProvider });

  const openDocusignSigning = useCallback(async () => {
    try {
      setIsLoading(true);

      const { data } = await api.shareableLinks.getNDADocusignUrl(
        shareableLinkId,
      );

      if (data?.url) {
        window.location.href = data.url;
      } else {
        setIsLoading(false);
      }
    } catch (e) {
      popServerError(e);
      setIsLoading(false);
    }
  }, [shareableLinkId, popServerError]);

  const onNativeSigningError = useCallback(
    (error: AxiosError) => {
      switch (getAgreementErrorCode(error)) {
        case AGREEMENT_ERROR_CODES.LAST_NAME_REQUIRED:
        case AGREEMENT_ERROR_CODES.ALREADY_EXECUTED:
          onRefetch?.();
          break;
        default:
          popServerError(error);
      }
    },
    [onRefetch, popServerError],
  );

  const openNativeSigning = useCallback(
    async (shouldReplace = false) => {
      try {
        setIsLoading(true);

        const {
          data: { url },
        } = await api.shareableLinks.getNdaSigningLink(shareableLinkId);

        navigate(
          getSigningPathWithReturnPath(
            url,
            getShareableLinkRoute(shareableLinkId),
          ),
          { replace: shouldReplace },
        );
      } catch (e) {
        onNativeSigningError(e as AxiosError);
        setIsLoading(false);
      }
    },
    [navigate, onNativeSigningError, shareableLinkId],
  );

  useEffect(() => {
    if (shouldStartSigning) openNativeSigning(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isProcessing) {
    return <ShareableLinkNdaProcessing />;
  }

  return (
    <ShareableLinkLockScreen className={classes} title={t('signNdaToAccess')}>
      <Button
        variant="outline"
        disabled={isLoading}
        onClick={isNativeNda ? () => openNativeSigning() : openDocusignSigning}
      >
        {isLoading ? (
          <Loader size="small" />
        ) : (
          <>
            <LockclosedIcon />
            {t('reviewAndSignNda')}
          </>
        )}
      </Button>
    </ShareableLinkLockScreen>
  );
};

export default ShareableLinkNdaSign;
